<template>
  <el-row
    v-loading.fullscreen.lock="fullscreenLoading"
    class="pre-wrap"
  >
    <h3>
      {{ $t('preCheckIn.uploadPhoto.title') }}
    </h3>
    <el-row class="info">
      {{ $t('preCheckIn.uploadPhoto.formTitle') }}
      <br>
      <span class="subtitle">{{ $t('preCheckIn.uploadPhoto.formSubtitle') }}</span>
    </el-row>
    <el-form
      :model="userData.address"
      :rules="rules"
      ref="userPhotoForm"
      label-position="top"
      class="upload-photo pre-reg"
      autocomplete="off"
    >
      <el-form-item
        class="is-mobile row"
        prop="frontSide"
      />
      <el-row
        v-if="loadedData"
        :class="['block front', {'show-webcam': frontWebcam}, {'loaded-image': isFrontLoaded}]"
      >
        <el-form-item
          :label="$t('common.labels.side.label', { side: $t('common.labels.side.front.name').toUpperCase(), type: $t('preCheckIn.uploadPhoto.label') })"
          prop="frontSide"
          class="document-wrap"
          v-if="typeof userData.address.frontSide !== 'undefined'"
        >
          <div
            class="upload-title"
            v-show="!isFrontLoaded && !frontWebcam"
          >
            <i18n
              path="common.labels.side.title"
            >
              <template v-slot:side>
                <b>{{ $t('common.labels.side.front.name') }}</b>
              </template>
              <template v-slot:type>
                {{ $t('preCheckIn.uploadPhoto.label') }}
              </template>
            </i18n>
          </div>
          <CameraCapture
            ref="frontCameraCapture"
            :side="'front'"
            :is-loaded="isFrontLoaded"
            :webcam-active="frontWebcam"
            :photo-taken="frontPhoto"
            :is-webcam-loaded="isWebcamLoaded"
            @activateCamera="activateCamera"
            @takePhoto="takePhoto"
          />
          <PreUploader
            :base64.sync="userData.address.frontSide"
            :photo.sync="frontSideFile"
            side="front"
            :is-draggable="false"
            @imageUploaded="frontSideUploaded"
            :class="[{'loaded-front': isFrontLoaded}, {'show-webcam': frontWebcam}]"
          />
        </el-form-item>
      </el-row>
      <el-form-item
        class="is-mobile row"
        prop="backSide"
      />
      <el-row
        v-if="loadedData"
        :class="['block', {'show-webcam': backWebcam}, {'loaded-image': isBackLoaded}]"
      >
        <el-form-item
          :label="$t('common.labels.side.label', { side: $t('common.labels.side.back.name').toUpperCase(), type: $t('preCheckIn.uploadPhoto.label') })"
          prop="backSide"
          class="document-wrap"
          v-if="typeof userData.address.backSide !== 'undefined'"
        >
          <div
            class="upload-title"
            v-show="!isBackLoaded && !backWebcam"
          >
            <i18n
              path="common.labels.side.title"
            >
              <template v-slot:side>
                <b>{{ $t('common.labels.side.back.name') }}</b>
              </template>
              <template v-slot:type>
                {{ $t('preCheckIn.uploadPhoto.label') }}
              </template>
            </i18n>
          </div>
          <CameraCapture
            ref="backCameraCapture"
            :side="'back'"
            :is-loaded="isBackLoaded"
            :webcam-active="backWebcam"
            :photo-taken="backPhoto"
            :is-webcam-loaded="isWebcamLoaded"
            @activateCamera="activateCamera"
            @takePhoto="takePhoto"
          />
          <PreUploader
            :base64.sync="userData.address.backSide"
            :is-draggable="false"
            @imageUploaded="backSideUploaded"
            :class="[{'loaded-back': isBackLoaded}, {'show-webcam': backWebcam}]"
          />
        </el-form-item>
      </el-row>
      <el-form-item class="submit-container">
        <el-form-item class="submit-btn">
          <el-button
            type="primary"
            @click="submitForm('userPhotoForm')"
          >
            {{ $t('common.buttons.next') }}
          </el-button>
        </el-form-item>
      </el-form-item>
    </el-form>
  </el-row>
</template>

<script>
import PreUploader from '../form/PreUploader';
import CameraCapture from '../form/CameraCapture.vue';
import store from "../../store";

export default {
  name: 'UploadPhoto',
  components: {
    PreUploader,
    CameraCapture
  },
  data: () => {
    return {
      userData: {},
      updatedPatientData: {},
      updatedAppData: {},
      loadedData: false,
      isDataUpdated: false,
      fullscreenLoading: false,
      file: null,
      isFrontLoaded: false,
      isBackLoaded: false,
      frontSideFile: null,
      frontSideUploadCount: 0,
      backSideUploadCount: 0,
      patientNumber: null,
      activeStep: null,
      sid: null,
      sides: [
        'front',
        'back',
      ],
      routerName: 'PersonalInfo',
      rules: {},
      frontWebcam: false,
      backWebcam: false,
      frontWebcamStream: null,
      backWebcamStream: null,
      frontPhoto: false,
      backPhoto: false,
      isWebcamLoaded: false,
    };
  },
  mounted() {
    this.setProgressBar();
  },
  created () {
    this.setRulesMessage();
    this.activeStep = this.$route.path.substring(1).split('/')[1];
    this.patientNumber = store.getters.patientData.patientNumber;
    this.sid = this.getSessionId();
    if (!this.patientNumber) {
      this.fullscreenLoading = true;
      this.getDataByPatientNumber().then(() => {
        this.patientNumber = store.getters.patientData.patientNumber;
        this.setInsuranceCardData();
      }).catch(() => {
        this.fullscreenLoading = false;
        this.setMessage(this.$t('common.errors.fail'));
      });
    } else {
      this.setInsuranceCardData();
    }
  },
  methods: {
    activateCamera(type = false) {
      this.$nextTick(() => {
        this.startCamera(type);
      });
    },

    startCamera(type) {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const constraints = {
          video: {
            facingMode: 'environment',
            frameRate: { ideal: 30 },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        };

        navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
          this.$nextTick(() => {
            this.$refs[`${type}CameraCapture`].$refs[`${type}Video`].srcObject = stream;

              this[`${type}WebcamStream`] = stream;
              this.isWebcamLoaded = true;
              this.$set(this.userData.address, `${type}Side`, '');
              this[`${type}Webcam`] = !this[`${type}Webcam`];
              const key = type.charAt(0).toUpperCase() + type.slice(1);
              this[`is${key}Loaded`] = false;
          });
            })
            .catch((error) => {
              console.error('Error ', error);
            });
      } else {
        alert('Your browser does not support access to the camera');
      }
    },
    stopCamera(type = null) {
      const videoStream = this[`${type}WebcamStream`];

      if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
      }

      this[`${type}WebcamStream`] = null;
      this[`${type}Webcam`] = false;
      this.isWebcamLoaded = false;
      this[`${type}Photo`] = false;
    },

    takePhoto(type = null) {
      const key = type.charAt(0).toUpperCase() + type.slice(1);

      const video =  this.$refs[`${type}CameraCapture`].$refs[`${type}Video`];
      const canvas =  this.$refs[`${type}CameraCapture`].$refs.canvas;
      const overlay =  this.$refs[`${type}CameraCapture`].$refs.frameOverlay.$el;
      const ctx = canvas.getContext("2d");

      const { videoWidth: vW, videoHeight: vH } = video;
      const { width: vRW, height: vRH, left: vL, top: vT } = video.getBoundingClientRect();
      const { width: oW, height: oH, left: oL, top: oT } = overlay.getBoundingClientRect();

      const scaleX = vW / vRW, scaleY = vH / vRH;
      const cropX = (oL - vL) * scaleX, cropY = (oT - vT) * scaleY;
      const cropW = oW * scaleX, cropH = oH * scaleY;

      canvas.width = oW;
      canvas.height = oH;
      ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, oW, oH);

      if (!type) return;

      const photoData = canvas.toDataURL("image/jpeg", 0.9);


      Object.assign(this, {
        [`${type}Photo`]: photoData,
        [`is${key}Loaded`]: true,
        [`${type}Webcam`]: false
      });

      this.userData.address[`${type}Side`] = photoData;
      this[`${type}SideUploaded`](true);

      this.stopCamera(type);
    },
    setInsuranceCardData() {
      this.saveStatisticData(this.sid, {
        active_step: this.activeStep,
        render_UploadPhoto : 'true'
      });
      this.userData = store.getters.patientData;
      this.$set(this.userData, 'address', {
        frontSide: '',
        backSide: ''
      });
      this.updateUserAppData(this.userData.patientId, {
        active_pr_step: this.activeStep,
        active_route: this.$route.name
      });
      this.loadedData = true;
      this.fullscreenLoading = false;
    },
    setRulesMessage() {
      this.sides.forEach((item) => {
        this.$set(this.rules, item + 'Side', {
          required: true,
          message: this.$t('common.rules.emptyPhoto', { side: this.capitalized(this.$t('common.labels.side.' + item +'.name')) }),
          trigger: 'change'
        });
      });
    },
    setUpdatedDataFromOcr(data) {
      var address = {};
      data.fields.map((field) => {
        switch (field.name) {
          case 'ZipCode':
            Object.assign(address, {zip: field.value});
            break;
          case 'Street':
            Object.assign(address, {street1: field.value});
            break;
          case 'City':
            Object.assign(address, {city: field.value});
            break;
          case 'State':
            Object.assign(address, {state: field.value});
            break;
          case 'FirstName':
            Object.assign(this.updatedAppData, {fname: field.value});
            break;
          case 'LastName':
            Object.assign(this.updatedAppData, {lname: field.value});
            break;
          case 'DateOfBirth':
            Object.assign(this.updatedAppData, {dob: field.value});
            break;
          case 'Sex':
            Object.assign(this.updatedAppData, {gender: field.value});
            break;
        }
      });
      Object.assign(this.userData.address, address);
      Object.assign(this.updatedAppData, address);
    },
    frontSideUploaded (fromCamera) {
      this.isFrontLoaded = true;
      this.fullscreenLoading = true;

      if (!fromCamera) {
        this.frontPhoto = false;
        this.frontWebcam = false;
        this.stopCamera();
      }

      this.$refs.userPhotoForm.clearValidate("frontSide");

      this.$axios('/Ocr/driverlicense', {
        method: 'POST',
        data: {
          blobData: this.userData.address.frontSide.split(',')[1],
          state: this.userData.address.state
        }
      }).then((response) => {
        if (response.data.isSuccess && response.data.data.status === 'Success') {
          this.setUpdatedDataFromOcr(response.data.data);
          this.isDataUpdated = true;
          this.fullscreenLoading = false;
          this.saveStatisticData(this.sid, {
            fs_ocr_loaded: 'true'
          });
        } else {
          this.fullscreenLoading = false;
          this.setMessage(this.$t('common.errors.notRecognize', { type: this.$t('common.labels.side.label', { side: this.$t('common.labels.side.front.name') }) }), 'warning');
        }
        this.frontSideUploadCount++;
        this.saveStatisticData(this.sid, {
          fs_document_upload: this.frontSideUploadCount
        });
        this.fullscreenLoading = false;
      }).catch(() => {
        this.fullscreenLoading = false;
        this.setMessage(this.$t('common.errors.fail'));
      });
    },
    backSideUploaded (fromCamera = false) {
      if (!fromCamera) {
        this.backPhoto = false;
        this.backWebcam = false;
      }

      this.$refs.userPhotoForm.clearValidate("backSide");

      this.backSideUploadCount++;
      this.saveStatisticData(this.sid, {
        bs_document_upload: this.backSideUploadCount
      });
      this.isBackLoaded = true;
      this.fullscreenLoading = false;
    },
    submitForm(formName) {
      this.fullscreenLoading = true;
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.saveStatisticData(this.sid, {
            submit_UploadPhoto: 'true'
          });
          this.savePatientData();
        } else {
          this.fullscreenLoading = false;
          return false;
        }
      });
    },
    saveDriverLicense() {
      this.saveDriverLicenseFront().then((response) => {
        if (response.data.isSuccess) {
          this.saveStatisticData(this.sid, {
            fs_document_save: 'true'
          });
          this.saveDriverLicenseBack().then((response) => {
            if (response.data.isSuccess) {
              this.saveStatisticData(this.sid, {
                bs_document_save: 'true'
              });
              this.fullscreenLoading = false;
              this.$router.push({
                name: this.routerName,
                params: {
                  patientNumber: this.patientNumber
                },
              });
            } else {
              this.fullscreenLoading = false;
              this.setMessage(
                this.$t('common.errors.save', { type: this.$t('preCheckIn.uploadPhoto.title') }),
                'warning'
              );
            }
          });
        } else {
          this.fullscreenLoading = false;
          this.setMessage(this.$t('common.errors.save', { type: this.$t('preCheckIn.uploadPhoto.title') }));
        }
      });
    },
    savePatientData() {
      this.fullscreenLoading = true;
      this.updateStepsData(this.userData.patientId, {[this.activeStep] : 1}).then((response) => {
        if (response.data.isSuccess) {
          if (!this.isObjEmpty(this.updatedAppData)) {
            this.updateUserAppData(this.userData.patientId, {
              driverLicenceData: this.updatedAppData
            }).then((response) => {
              if (response.data.isSuccess) {
                this.saveDriverLicense();
              }
            });
          } else {
            this.saveDriverLicense();
          }
        }
      });
    },
    saveDriverLicenseFront() {
      return this.$axios('/Patient/SavePatientDriverLicenseImage', {
        method: 'PUT',
        data: {
          patientNumber: this.patientNumber,
          blobData: this.userData.address.frontSide.split(',')[1],
          isFrontImage: true
        }
      }).catch(() => {
        this.fullscreenLoading = false;
        this.setMessage(this.$t('common.errors.fail'));
      });
    },
    saveDriverLicenseBack() {
      return this.$axios('/Patient/SavePatientDriverLicenseImage', {
        method: 'PUT',
        data: {
          patientNumber: this.patientNumber,
          blobData: this.userData.address.backSide.split(',')[1],
          isFrontImage: false
        }
      }).catch(() => {
        this.fullscreenLoading = false;
        this.setMessage(this.$t('common.errors.fail'));
      });
    }
  }
};
</script>
